import { Component, OnDestroy, Input, Output, EventEmitter, ViewChild, OnInit, inject } from '@angular/core';
import { VoiceHint } from 'src/app/_bio-check/voice-check/voice-hint.enum';
import { BioCheckErrorCode } from 'src/app/_bio-check/bio-check-error-code.enum';
import { AudioLevelMeter } from '../voice-visualiser/audio-level-meter';
import { FileService } from '@shared/services/file';
import { MediaDevicesService } from '@shared/services/media-devices';
import { MediaRecorderComponent } from '../../media-recorder/media-recorder.component';
import { CheckProgress, Recording } from '../models';
import { LoadingStatus } from '@shared/models/loading-status.enum';
import { DialogService } from '@shared/services/dialog';
import { type StoppedRecordingEvent } from '../voice-record-button/voice-record-button.component';
import { HelpVideoDialogComponent } from '../help-video-dialog/help-video-dialog.component';
import { TimeUtil } from '@shared/utils/time.util';
import { MicrophoneTroubleshootDialogComponent } from '../microphone-troubleshoot-dialog/microphone-troubleshoot-dialog.component';
import { StoreService } from '@shared/store';
import { selectDebugSettings } from '@shared/store/slices/debug';
import { WINDOW } from '@shared/dom';

@Component({
  selector: 'app-voice-record',
  templateUrl: './voice-record.component.html',
  styleUrls: ['./voice-record.component.scss'],
  standalone: false,
})
export class VoiceRecordComponent implements OnInit, OnDestroy {
  private readonly mediaDevices = inject(MediaDevicesService);
  private readonly store = inject(StoreService);
  private readonly fileService = inject(FileService);
  private readonly dialog = inject(DialogService);
  private readonly window: Window = inject(WINDOW);

  public readonly ErrorCode = BioCheckErrorCode;
  public readonly VoiceHint = VoiceHint;
  public readonly LoadingStatus = LoadingStatus;
  /** Recordings under this time are automatically discarded. */
  public readonly MIN_RECORDING_TIME = TimeUtil.toMilliseconds({ seconds: 1.5 });
  /** Recordings over this time are automatically discarded. */
  public readonly MAX_RECORDING_TIME = TimeUtil.toMilliseconds({ seconds: 10 });
  /** Recordings start showing a warning to stop recording at this point. */
  public readonly SHOW_STOP_RECORDING_CTA = TimeUtil.toMilliseconds({ seconds: 5.5 });

  /** An override locale for the voice phrase. */
  @Input() locale: string | undefined;
  /** Check progress, so that we can determine which hint to show. */
  @Input() checks: CheckProgress | undefined;
  /** Recording status, so that we can reflect progress in the UI. */
  @Input() recordings: Recording[] = [];
  /** Whether the voice component is waiting on uploads to complete. */
  @Input() waitingForUploads = false;
  /** Whether the voice component is polling for results. */
  @Input() polling = false;
  @Input() showStatuses = true;
  /** Whether this result is being checked by the host component. */
  @Input() isPendingEvaluation = false;
  @Output() fileReady = new EventEmitter<Blob>(true);
  @Output() obtainMediaFailed = new EventEmitter();

  @ViewChild('recorder', { static: true }) recorder: MediaRecorderComponent;

  public readonly meter = new AudioLevelMeter();

  public firstRecording = true;
  public overrideHint: VoiceHint | undefined;
  public stream: MediaStream | undefined;
  public isRecording = false;
  public recordingStartTime = 0;
  private locked = false;

  /** Whether the user's capacity to record audio should be disabled. */
  public get recordingEnabled() {
    return !this.polling && !this.isPendingEvaluation && !this.locked;
  }

  ngOnInit(): void {
    this.getStream();
  }

  /**
   * Whether the UI should show the “tap to stop recording” call to action reminder.
   *
   * This reminder only shows up several seconds into the very first training recording.
   */
  public shouldShowStopRecordingCta() {
    if (!this.isRecording) {
      return false;
    }

    const isFirstTrainingRecording = this.checks?.training === 0 && !this.checks.trainingDone;
    if (!isFirstTrainingRecording) {
      return false;
    }

    return Date.now() > this.recordingStartTime + this.SHOW_STOP_RECORDING_CTA;
  }

  async getStream() {
    try {
      this.stream = await this.mediaDevices.getAudioStream();
      this.meter.start(this.stream);
    } catch {
      this.obtainMediaFailed.emit();
    }
  }

  public getHint() {
    if (this.overrideHint) {
      return this.overrideHint;
    }

    if (this.locked || this.isPendingEvaluation) {
      // Pretend we're still checking with the server.
      return VoiceHint.Polling;
    }

    if (this.waitingForUploads) {
      return VoiceHint.WaitingForUploads;
    }

    if (this.polling) {
      return VoiceHint.Polling;
    }

    if (this.firstRecording) {
      return VoiceHint.FirstRecording;
    }

    if (!this.checks?.trainingDone) {
      return VoiceHint.Training2;
    } else if (!this.checks.verificationDone) {
      switch (this.checks.verification) {
        case 0:
          return VoiceHint.Verification1;
        default:
          return VoiceHint.Verification2;
      }
    }
  }

  getCurrentUpload() {
    return this.recordings.findLast((r) => r.upload);
  }

  public startRecording() {
    this.recorder.startRecording(this.stream);
    this.meter.resume();
    this.meter.resetPeak();
    this.isRecording = true;
    this.recordingStartTime = Date.now();
  }

  /**
   * Response to a signal the user has ended the recording.
   */
  public async endRecording(event: StoppedRecordingEvent) {
    if (!this.recorder) {
      throw Error("Can't stop recording. There is no recorder. (How did you start recording!?)");
    }
    this.isRecording = false;
    this.firstRecording = false;
    this.overrideHint = undefined;
    const file = await this.recorder.stopRecording();
    const recordingEndTime = Date.now();
    const duration = recordingEndTime - this.recordingStartTime;
    const debug = this.store.select(selectDebugSettings());
    if (debug.enabled && debug.downloadMedia) {
      this.fileService.downloadToDevice(file, 'sample.webm');
    }
    if (duration < this.MIN_RECORDING_TIME) {
      console.warn('Recording too short. Discarded.');
      this.overrideHint = VoiceHint.TooShort;
      return;
    }
    if (event.expired || duration >= this.MAX_RECORDING_TIME) {
      console.warn('Recording too long. Discarded.');
      this.overrideHint = VoiceHint.TooLong;
      this.openHelpVideoDialog();
      return;
    }
    if (this.meter.isTooQuiet()) {
      console.warn("Recording too quiet. Discarded.")
      this.overrideHint = VoiceHint.TooQuiet;
      this.openMicrophoneTroubleshootDialog();
      return;
    }
    this.lock();
    this.fileReady.emit(file);
  }

  /** Lock this component, assuming there will be an upload processing. */
  private lock() {
    this.locked = true;
  }

  /** Unlock this component for recording. */
  public unlock() {
    this.locked = false;
  }

  /**
   * Open the help video dialog. This only actually shows if there's no completed recordings.
   *
   * This dialog explains how to use voice check to people who are struggling with it.
   */
  private openHelpVideoDialog() {
    if (this.checks?.training === 0 && this.checks.verification === 0) {
      this.dialog.resolve(HelpVideoDialogComponent, {
        autoFocus: true,
        disableClose: false,
        panelClass: 'zero-padding-dialog',
        exitAnimationDuration: 500,
      });
    }
  }

  private async openMicrophoneTroubleshootDialog() {
    const result = await this.dialog.resolve<boolean>(MicrophoneTroubleshootDialogComponent, {
      autoFocus: true,
      disableClose: false,
      exitAnimationDuration: 500,
    });
    if (result === true) {
      this.window.location.reload();
    }
  }

  ngOnDestroy() {
    this.meter.stop();
    if (this.stream) {
      this.mediaDevices.stopStream(this.stream);
    }
  }
}
